import { refreshToken } from '@hermes/api-sdk'
import { useAuthStore } from '@lib/stores/auth'
import { isAxiosError } from 'axios'
import { useEffect, useRef } from 'react'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import { useSignOut } from './useSignOut'
import { useUserToken } from './useUserToken'
import { useLocale } from './useLocale'

const REFRESH_INTERVAL = 60000 // 1 minute
const REFRESH_MARGIN = 120000 // 2 minutes

const useTokenValidation = () => {
  const accessJWT = useAuthStore((state) => state.accessJWT)
  const refreshJWT = useAuthStore((state) => state.refreshJWT)
  const setTokenExpiresAt = useAuthStore((state) => state.setTokenExpiresAt)
  const { signOut } = useSignOut()
  const { setToken } = useUserToken()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const failureCountRef = useRef<number>(0)
  const lastSignOutAttemptRef = useRef<number>(0)
  const lastRefreshAttemptRef = useRef<number>(0)
  const locale = useLocale()
  const region = getRegionFromLocale(locale)

  const stopRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const refresh = async () => {
    const now = Date.now()

    // Rate limit: only allow one refresh attempt per 5 seconds
    if (now - lastRefreshAttemptRef.current < 5000) {
      return false
    }

    lastRefreshAttemptRef.current = now

    const { tokenExpiresAt } = useAuthStore.getState() // needs to be reactive each time
    if (tokenExpiresAt && tokenExpiresAt > Date.now() + REFRESH_MARGIN) {
      return true
    }
    try {
      const result = await refreshToken({ accessJWT, refreshJWT }, { tenant: region })
      setToken(result.accessToken)
      setTokenExpiresAt(Date.now() + result.expiresIn * 1000)
      failureCountRef.current = 0 // Reset failure count on success
      return true
    } catch (error) {
      failureCountRef.current += 1

      if (isAxiosError(error)) {
        // Only sign out after multiple failures and prevent rapid successive sign-out attempts
        const shouldSignOut = failureCountRef.current >= 3 && now - lastSignOutAttemptRef.current > 30000 // 30 seconds cooldown

        if (shouldSignOut) {
          lastSignOutAttemptRef.current = now
          stopRefresh()
          signOut()
        }
      }
      return false
    }
  }

  const startRefresh = () => {
    if (intervalRef.current) {
      return
    }

    intervalRef.current = setInterval(() => {
      refresh()
    }, REFRESH_INTERVAL)
  }

  useEffect(
    () => () => {
      stopRefresh()
    },
    []
  )

  return {
    refresh,
    startRefresh,
    stopRefresh
  }
}

export { useTokenValidation }
